<template>
    <div class="page links-page">
        <div class="links-bg" aria-hidden="true">
            <div class="links-bg__orb links-bg__orb--1" />
            <div class="links-bg__orb links-bg__orb--2" />
            <div class="links-bg__orb links-bg__orb--3" />
            <div class="links-bg__grid" />
        </div>

        <v-container class="links-content py-8 py-lg-12 px-3">
            <div class="links-wrapper mx-auto">
                <header class="links-hero text-center mb-8 mb-lg-10">
                    <div class="logo-ring mx-auto mb-5">
                        <div class="logo-ring__glow" />
                        <div class="logo-ring__inner">
                            <logo :link="false" :height="68" />
                        </div>
                    </div>

                    <div class="links-badge mb-4">
                        <span class="links-badge__dot" />
                        <span>تابعنا على كل المنصات</span>
                    </div>

                    <h1 class="links-title font-weight-bold mb-3">روابطنا الرسمية</h1>
                    <p class="links-subtitle muted--text mb-0">
                        Coding With Ghonaim — محتوى تعليمي في البرمجة والذكاء الاصطناعي
                    </p>
                </header>

                <section v-if="socialItems.length" class="links-social-section text-center mb-8 mb-lg-10">
                    <h2 class="links-section-title strong-text--text mb-4">حسابات التواصل</h2>
                    <div class="links-social">
                        <a
                            v-for="(item, index) in socialItems"
                            :key="item.brand + '-' + index"
                            :href="item.href"
                            :aria-label="item.label"
                            :title="item.label"
                            :target="capRunning ? '_system' : '_blank'"
                            rel="noopener noreferrer"
                            class="links-social__link"
                            :style="{ '--social-brand-color': item.color }"
                        >
                            <welcome-social-brand-icon :brand="item.brand" :size="20" />
                        </a>
                    </div>
                </section>

                <section v-if="linkCards.length || loadingLinks" class="links-list-section">
                    <h2 v-if="linkCards.length" class="links-section-title strong-text--text text-center mb-5">
                        روابط مهمة
                    </h2>

                    <v-progress-linear
                        v-if="loadingLinks"
                        indeterminate
                        color="secondary"
                        height="2"
                        class="mb-4"
                    />

                    <div v-if="linkCards.length" class="links-list">
                        <a
                            v-for="(item, i) in linkCards"
                            :key="item.id"
                            class="link-card"
                            :style="{ animationDelay: `${0.12 + i * 0.1}s` }"
                            :href="item.url"
                            :target="capRunning ? '_system' : '_blank'"
                            rel="noopener noreferrer"
                        >
                            <div class="link-card__accent" :style="{ background: item.bg }" />
                            <div class="link-card__shine" />

                            <div class="link-card__body d-flex align-center pa-4 pa-lg-5">
                                <div class="link-icon me-4" :style="{ background: item.bg, boxShadow: item.glow }">
                                    <img
                                        v-if="item.image_url"
                                        :src="item.image_url"
                                        :alt="item.label"
                                        class="link-icon__image"
                                    />
                                    <welcome-social-brand-icon
                                        v-else-if="item.brand"
                                        :brand="item.brand"
                                        :size="26"
                                    />
                                    <v-icon v-else color="white" size="28">mdi-link-variant</v-icon>
                                </div>

                                <div class="link-text flex-grow-1">
                                    <div class="link-label font-weight-bold strong-text--text">{{ item.label }}</div>
                                    <div v-if="item.subtitle" class="link-subtitle muted--text mt-1">{{ item.subtitle }}</div>
                                </div>

                                <div class="link-action ms-2">
                                    <span class="link-action__pill" :style="{ background: item.bg }">
                                        <v-icon size="16" color="white">mdi-arrow-left</v-icon>
                                    </span>
                                </div>
                            </div>
                        </a>
                    </div>
                </section>

                <div
                    v-if="!loadingLinks && !socialItems.length && !linkCards.length"
                    class="links-empty pa-4 text-center"
                >
                    <v-alert type="info" color="blue-grey" text class="mb-0">
                        <div class="details-text">لا توجد روابط متاحة حالياً</div>
                    </v-alert>
                </div>

                <footer v-if="socialItems.length || linkCards.length" class="links-footer text-center mt-10">
                    <div v-if="socialItems.length" class="links-footer__icons d-flex justify-center align-center mb-3">
                        <span
                            v-for="item in socialItems"
                            :key="`dot-${item.brand}`"
                            class="links-footer__icon"
                            :style="{ background: item.color }"
                        />
                    </div>
                    <p class="links-footer__text muted--text caption mb-0">
                        اضغط على أي رابط للانتقال مباشرة
                    </p>
                </footer>
            </div>
        </v-container>

        <slot v-if="!isAuthed" name="footer" />
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed';
import GlobalMethods from '@/helpers/global-methods';
import WelcomeSocialBrandIcon from '@/components/welcome/landing/WelcomeSocialBrandIcon.vue';
import {
    buildLinksPageSocialItems,
    getCustomLinkCards,
    getCustomLinksFromAppData,
    normalizeCustomLinkCards,
} from '@/helpers/links-page-data';

export default {
    components: {
        WelcomeSocialBrandIcon,
    },
    data() {
        return {
            fetchedLinks: null,
            loadingLinks: false,
        };
    },
    computed: {
        socialItems() {
            return buildLinksPageSocialItems(this.appData);
        },
        linkCards() {
            const extra = this.fetchedLinks || [];
            return getCustomLinkCards(this.appData, extra);
        },
        ...GlobalComputed,
    },
    watch: {
        appData: {
            handler() {
                if (this.fetchedLinks === null && !this.loadingLinks && !getCustomLinksFromAppData(this.appData).length) {
                    this.fetchCustomLinksFromApi();
                }
            },
            immediate: true,
        },
    },
    methods: {
        ...GlobalMethods,
        fetchCustomLinksFromApi() {
            if (this.loadingLinks || this.fetchedLinks !== null) {
                return;
            }

            this.loadingLinks = true;
            window.axios.get('/links')
                .then((response) => {
                    this.fetchedLinks = normalizeCustomLinkCards(response.data);
                })
                .catch(() => {
                    this.fetchedLinks = [];
                })
                .finally(() => {
                    this.loadingLinks = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
$brand-red: #e33434;
$brand-red-light: #f26b5a;
$brand-navy: #1a0e10;

.links-page {
    position: relative;
    min-height: 100%;
    overflow: visible;
    display: flex;
    flex-direction: column;
}

.links-bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    overflow: hidden;
    background:
        radial-gradient(ellipse 80% 50% at 50% -10%, rgba($brand-red, 0.12), transparent 60%),
        linear-gradient(180deg, #fff7f6 0%, #fafafa 45%, #f5f5f5 100%);

    &__orb {
        position: absolute;
        border-radius: 50%;
        filter: blur(70px);
        opacity: 0.55;
        animation: links-float 8s ease-in-out infinite;

        &--1 {
            width: 320px;
            height: 320px;
            top: -80px;
            right: -60px;
            background: rgba($brand-red, 0.22);
        }

        &--2 {
            width: 280px;
            height: 280px;
            top: 35%;
            left: -90px;
            background: rgba($brand-red-light, 0.18);
            animation-delay: -2.5s;
        }

        &--3 {
            width: 240px;
            height: 240px;
            bottom: 10%;
            right: 10%;
            background: rgba(42, 171, 238, 0.14);
            animation-delay: -4s;
        }
    }

    &__grid {
        position: absolute;
        inset: 0;
        opacity: 0.35;
        background-image:
            linear-gradient(rgba($brand-navy, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba($brand-navy, 0.04) 1px, transparent 1px);
        background-size: 32px 32px;
        mask-image: radial-gradient(ellipse 70% 60% at 50% 30%, black 20%, transparent 75%);
    }
}

.links-content {
    position: relative;
    z-index: 1;
    flex: 1 0 auto;
    padding-bottom: 2.5rem;
}

.links-page > .footer,
.links-page ::v-deep > .footer {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    margin-top: auto;
}

.links-wrapper {
    max-width: 460px;
}

.logo-ring {
    position: relative;
    width: 112px;
    height: 112px;
    display: flex;
    align-items: center;
    justify-content: center;

    &__glow {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: linear-gradient(135deg, $brand-red, $brand-red-light);
        opacity: 0.18;
        animation: links-pulse 3s ease-in-out infinite;
    }

    &__inner {
        position: relative;
        z-index: 1;
        width: 96px;
        height: 96px;
        border-radius: 50%;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow:
            0 0 0 4px rgba($brand-red, 0.12),
            0 12px 32px rgba($brand-navy, 0.1);
    }
}

.links-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba($brand-red, 0.15);
    box-shadow: 0 4px 16px rgba($brand-navy, 0.06);
    font-size: 13px;
    font-weight: 700;
    color: rgba($brand-navy, 0.75);
    backdrop-filter: blur(8px);
    animation: links-fade-up 0.6s ease-out both;

    &__dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #22c55e;
        box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.2);
        animation: links-blink 2s ease-in-out infinite;
    }
}

.links-title {
    font-size: 1.75rem;
    line-height: 1.3;
    background: linear-gradient(135deg, $brand-navy 0%, $brand-red 55%, $brand-red-light 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: links-fade-up 0.65s 0.08s ease-out both;
}

.links-subtitle {
    max-width: 340px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.8;
    animation: links-fade-up 0.65s 0.14s ease-out both;
}

.links-section-title {
    font-size: 1.05rem;
    font-weight: 700;
}

.links-social {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 12px;
    animation: links-fade-up 0.65s 0.2s ease-out both;

    &__link {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        border-radius: 12px;
        color: #fff;
        background: var(--social-brand-color, rgba($brand-navy, 0.85));
        border: 1px solid rgba($brand-navy, 0.08);
        text-decoration: none;
        box-shadow: 0 4px 14px rgba($brand-navy, 0.08);
        transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 24px -8px var(--social-brand-color, rgba($brand-navy, 0.25));
            filter: brightness(1.06);
        }
    }
}

.links-list {
    width: 100%;
}

.link-card {
    position: relative;
    display: block;
    text-decoration: none;
    border-radius: 18px;
    overflow: hidden;
    margin-bottom: 14px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba($brand-navy, 0.07);
    box-shadow: 0 4px 20px rgba($brand-navy, 0.06);
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    animation: links-fade-up 0.65s ease-out both;
    backdrop-filter: blur(10px);

    &:hover {
        transform: translateY(-4px) scale(1.01);
        box-shadow: 0 16px 40px rgba($brand-navy, 0.12);

        .link-card__shine {
            transform: translateX(120%);
        }

        .link-action__pill {
            transform: scale(1.08);
        }
    }

    &:active {
        transform: translateY(-1px) scale(1);
    }

    &__accent {
        position: absolute;
        top: 0;
        right: 0;
        width: 5px;
        height: 100%;
        border-radius: 0 18px 18px 0;
    }

    &__shine {
        position: absolute;
        inset: 0;
        background: linear-gradient(
            105deg,
            transparent 35%,
            rgba(255, 255, 255, 0.45) 50%,
            transparent 65%
        );
        transform: translateX(-120%);
        transition: transform 0.7s ease;
        pointer-events: none;
    }
}

.link-icon {
    width: 58px;
    height: 58px;
    min-width: 58px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    overflow: hidden;
    transition: transform 0.25s ease;

    .link-card:hover & {
        transform: scale(1.06) rotate(-3deg);
    }

    &__image {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.link-label {
    font-size: 1.05rem;
}

.link-subtitle {
    font-size: 0.82rem;
    line-height: 1.5;
}

.link-action__pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 12px;
    transition: transform 0.25s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.links-footer {
    animation: links-fade-up 0.65s 0.45s ease-out both;

    &__icons {
        gap: 10px;
    }

    &__icon {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        opacity: 0.85;
    }

    &__text {
        opacity: 0.75;
    }
}

@keyframes links-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-16px); }
}

@keyframes links-pulse {
    0%, 100% { transform: scale(1); opacity: 0.18; }
    50% { transform: scale(1.08); opacity: 0.28; }
}

@keyframes links-fade-up {
    from {
        opacity: 0;
        transform: translateY(18px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes links-blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
}

.theme--dark {
    .links-bg {
        background:
            radial-gradient(ellipse 80% 50% at 50% -10%, rgba($brand-red, 0.18), transparent 60%),
            linear-gradient(180deg, #141414 0%, #0f0f0f 100%);

        &__grid {
            opacity: 0.2;
            background-image:
                linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
        }
    }

    .logo-ring__inner {
        background: #1e1e1e;
        box-shadow:
            0 0 0 4px rgba($brand-red, 0.2),
            0 12px 32px rgba(0, 0, 0, 0.35);
    }

    .links-badge {
        background: rgba(255, 255, 255, 0.06);
        border-color: rgba(255, 255, 255, 0.1);
        color: rgba(255, 255, 255, 0.75);
    }

    .links-social__link {
        border-color: rgba(255, 255, 255, 0.08);
    }

    .link-card {
        background: rgba(255, 255, 255, 0.05);
        border-color: rgba(255, 255, 255, 0.08);
        box-shadow: none;

        &:hover {
            background: rgba(255, 255, 255, 0.08);
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .links-bg__orb,
    .logo-ring__glow,
    .links-badge,
    .links-badge__dot,
    .links-title,
    .links-subtitle,
    .links-social,
    .link-card,
    .links-footer {
        animation: none !important;
    }

    .link-card:hover {
        transform: none;
    }
}
</style>
